import {
	createContext,
	useCallback,
	useEffect,
	useMemo,
	useState,
	type FC,
	type ReactElement,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import {
	QuickStartContext,
	useQuickStart,
} from "../hooks/useQuickStart";
import { useReadOnlyAccess } from "../hooks/useReadOnlyAccess";
import { LitheSliceActions } from "../redux/lithe/lithe.slice";
import { fetchLitheThunk } from "../redux/lithe/lithe.thunk";
import {
	useAppDispatch,
	useAppSelector,
} from "../redux/store";
import { services } from "../services";
import {
	LitheCreationTypeEnum,
	LitheStepEnum,
} from "../types/lithe.type";
import { getApiErrorMessage } from "../utils/apiErrorUtils";
import {
	get_all_steps,
	getIncompleteSpecsQuestions,
	getLitheStepLabel,
	normalizeLitheStep,
} from "../utils/litheUtils";
import { toCurrentTenantPath } from "../utils/tenantUtils";

export const LitheContext = createContext<any>(null);

export const LitheContextProvider: FC<{
	children: ReactElement;
}> = ({ children }) => {
	const { method, step, litheId = "" } = useParams();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const isReadOnlyUser = useReadOnlyAccess();
	const { meta, specsQuestions } = useAppSelector(
		(state) => state.lithe
	);
	const [
		continueAndConfirmLoading,
		setContinueAndConfirmLoading,
	] = useState(false);
	const [stepScreenLoading, setStepScreenLoading] =
		useState(false);
	const [stepScreenStatus, setStepScreenStatus] =
		useState("idle");
	const litheCreationType =
		meta.data?.litheCreationType ?? method;
	const availableLitheSteps = useMemo(
		() => get_all_steps(meta.data?.allSteps ?? []),
		[meta.data?.allSteps]
	);
	const effectiveCurrentStep = meta.data?.currentStep;
	const isApproved =
		effectiveCurrentStep === LitheStepEnum.LITHEAPPROVED;
	const currentIndex =
		effectiveCurrentStep === LitheStepEnum.LITHEAPPROVED
			? availableLitheSteps.length
			: availableLitheSteps.indexOf(
					effectiveCurrentStep as LitheStepEnum
				);
	const routeIndex = availableLitheSteps.indexOf(
		step as LitheStepEnum
	);
	const isCompletedScreen =
		routeIndex >= 0 && currentIndex > routeIndex;
	const incompleteSpecsQuestions = useMemo(
		() => getIncompleteSpecsQuestions(specsQuestions.data),
		[specsQuestions.data]
	);
	const goToStep = useCallback(
		(backendStep: string) => {
			const next = normalizeLitheStep(backendStep);
			if (next)
				navigate(
					toCurrentTenantPath(
						`/lithe/${method}/${next}/${litheId}`
					),
					{ replace: true }
				);
		},
		[litheId, method, navigate]
	);
	const refreshMeta = useCallback(
		async () =>
			await dispatch(fetchLitheThunk(litheId)).unwrap(),
		[dispatch, litheId]
	);
	const seed = useMemo(
		() =>
			meta.data?.litheId === litheId
				? {
						quick_start: meta.data.quickStart,
						current_step: meta.data.currentStep,
					}
				: undefined,
		[litheId, meta.data]
	);
	const quickStartFlow = useQuickStart({
		kind: "lithe",
		id: litheId,
		seed,
		enabled: litheCreationType === LitheCreationTypeEnum.UG,
		refreshMeta,
		goToStep,
	});
	const { acceptResponse } = quickStartFlow;
	const shouldDisableEverything =
		isReadOnlyUser ||
		isApproved ||
		!meta.data ||
		isCompletedScreen ||
		quickStartFlow.isRunning ||
		litheCreationType !== LitheCreationTypeEnum.UG;
	const allLitheSteps = useMemo(
		() =>
			availableLitheSteps.map((stepId, index) => ({
				index,
				stepId,
				name: getLitheStepLabel(
					stepId,
					LitheCreationTypeEnum.UG
				),
				isCurrent: stepId === step,
				isCompleted: index < currentIndex,
				isAccessible: index <= currentIndex,
				isLocked: index > currentIndex,
			})),
		[availableLitheSteps, currentIndex, step]
	);
	const moveToStep = useCallback(
		(target: { stepId: string; isAccessible: boolean }) => {
			if (target.isAccessible) goToStep(target.stepId);
		},
		[goToStep]
	);

	useEffect(() => {
		if (
			meta.data &&
			currentIndex >= 0 &&
			(routeIndex < 0 || routeIndex > currentIndex) &&
			step !== LitheStepEnum.LITHEAPPROVED
		)
			goToStep(effectiveCurrentStep!);
	}, [
		currentIndex,
		effectiveCurrentStep,
		goToStep,
		meta.data,
		routeIndex,
		step,
	]);
	useEffect(() => {
		setContinueAndConfirmLoading(false);
	}, [litheId, step]);

	const continueAndConfirmQuestions =
		useCallback(async () => {
			if (
				shouldDisableEverything ||
				continueAndConfirmLoading
			)
				return;
			setContinueAndConfirmLoading(true);
			try {
				let response;
				switch (step) {
					case LitheStepEnum.SPECS:
						if (incompleteSpecsQuestions.length) return;
						response =
							await services.litheService.confirmSpecs(
								litheId
							);
						break;
					case LitheStepEnum.UGCLEANDATA:
						response =
							await services.litheService.processLitheCleanData(
								litheId,
								true
							);
						break;
					case LitheStepEnum.UGFILTERS:
						response =
							await services.litheService.processLitheFilters(
								litheId,
								true
							);
						break;
					case LitheStepEnum.UGTRANSDEF:
						response =
							await services.litheService.processLitheTransDef(
								litheId,
								true
							);
						break;
					case LitheStepEnum.UGFEATUREENG:
						response =
							await services.litheService.processLitheUgFeatureEngineering(
								litheId,
								true
							);
						break;
					case LitheStepEnum.UGREPLINES:
						response =
							await services.litheService.processLitheUgReplines(
								litheId,
								true
							);
						break;
					case LitheStepEnum.RFSPECS:
						response =
							await services.litheService.processLitheUgRFSpecs(
								litheId,
								true
							);
						break;
					case LitheStepEnum.APPROVAL:
						response =
							await services.litheService.confirmLitheApprovalInfo(
								litheId,
								true
							);
						break;
					default:
						return;
				}
				if (response?.status === "ERROR")
					throw new Error(
						response.error_msg ??
							"This screen could not be confirmed."
					);
				if (response?.quick_start?.status === "running") {
					await acceptResponse(response);
					return;
				}
				const refreshed = await refreshMeta();
				if (refreshed.quick_start?.status === "running") {
					await acceptResponse(refreshed);
				} else if (refreshed.current_step)
					goToStep(refreshed.current_step);
			} catch (error) {
				toast.error(
					getApiErrorMessage(
						error,
						"This screen could not be confirmed."
					)
				);
			} finally {
				setContinueAndConfirmLoading(false);
			}
		}, [
			continueAndConfirmLoading,
			goToStep,
			incompleteSpecsQuestions.length,
			litheId,
			acceptResponse,
			refreshMeta,
			shouldDisableEverything,
			step,
		]);

	const resetLitheAllStateAndPolling = useCallback(() => {
		dispatch(LitheSliceActions.resetLitheState());
	}, [dispatch]);
	const disableContinue =
		stepScreenLoading ||
		(step !== LitheStepEnum.APPROVAL &&
			stepScreenStatus !== "success") ||
		(step === LitheStepEnum.SPECS &&
			incompleteSpecsQuestions.length > 0);

	return (
		<LitheContext.Provider
			value={{
				method,
				step,
				litheId,
				meta,
				allLitheSteps,
				moveToStep,
				continueAndConfirmQuestions,
				resetLitheAllStateAndPolling,
				disableContinue,
				shouldDisableEverything,
				isReadOnlyUser,
				stepScreenLoading,
				setStepScreenLoading,
				stepScreenStatus,
				setStepScreenStatus,
				incompleteSpecsQuestions,
				continueAndConfirmLoading,
				setContinueAndConfirmLoading,
				litheCreationType,
				quickStartFlow,
				isCompletedScreen,
			}}
		>
			<QuickStartContext.Provider value={quickStartFlow}>
				{children}
			</QuickStartContext.Provider>
		</LitheContext.Provider>
	);
};
